import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { Color } from '../types';

interface ColorGridProps {
  colors: Color[];
  selectedColors: string[];
  onChange: (colors: string[]) => void;
  maxColors?: number;
  disabled?: boolean;
  label?: string;
  showCount?: boolean;
  size?: 'small' | 'medium';
  maxHeight?: number;
}

// Helper function to determine if a color is light
const isLightColor = (hex?: string): boolean => {
  if (!hex) return true;
  const color = hex.replace('#', '');
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
};

export const ColorGrid: React.FC<ColorGridProps> = ({
  colors,
  selectedColors,
  onChange,
  maxColors = 3,
  disabled = false,
  label,
  showCount = true,
  size = 'medium',
  maxHeight = 140,
}) => {
  const boxSize = size === 'small' ? 36 : 40;
  const checkmarkSize = size === 'small' ? 16 : 18;
  const gap = size === 'small' ? 0.5 : 0.5;

  const handleColorClick = (colorId: string) => {
    if (disabled) return;

    const isSelected = selectedColors.includes(colorId);
    const isAtMax = selectedColors.length >= maxColors && !isSelected;

    if (isAtMax) return;

    if (isSelected) {
      onChange(selectedColors.filter((c) => c !== colorId));
    } else {
      onChange([...selectedColors, colorId]);
    }
  };

  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
          {showCount && selectedColors.length > 0 && ` - ${selectedColors.length} selected`}
        </Typography>
      )}
      <Paper variant="outlined" sx={{ p: 1, maxHeight, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap }}>
          {colors.map((color) => {
            const isSelected = selectedColors.includes(color.id);
            const isDisabled = disabled || (selectedColors.length >= maxColors && !isSelected);
            const textColor = isLightColor(color.hex) ? '#000' : '#fff';

            return (
              <Box
                key={color.id}
                onClick={() => handleColorClick(color.id)}
                sx={{
                  width: boxSize,
                  height: boxSize,
                  bgcolor: color.hex || '#ccc',
                  borderRadius: 0.5,
                  border: isSelected ? 2 : 1, borderColor: isSelected ? 'primary.main' : 'divider',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.4 : 1,
                  transition: 'all 0.1s',
                  '&:hover': !isDisabled ? { transform: 'scale(1.1)', boxShadow: 1 } : {},
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={color.label}
              >
                {isSelected && (
                  <Box sx={{ color: textColor, fontSize: checkmarkSize, fontWeight: 'bold' }}>
                    ✓
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default ColorGrid;
